import {
  ForemanEmployeeDispatch,
  ForemanEquipmentDispatch,
  ForemanJobDetail,
  ForemanPhase,
} from '@ups/xplat/api/dto';
import {
  dedupeForemanJobsByIdentity,
  getForemanJobDetailKey,
  jobsShareForemanIdentity,
} from './foreman-job-key.utils';
import { dedupeSelfEntryEmployeeDispatches } from './foreman-individual-entry-employee.utils';

const SELF_ENTRY_STATUSES = new Set(['Self-Entry', 'Individual-Entry']);

function deepCloneJob(job: ForemanJobDetail): ForemanJobDetail {
  if (typeof structuredClone === 'function') {
    return structuredClone(job) as ForemanJobDetail;
  }

  return JSON.parse(JSON.stringify(job)) as ForemanJobDetail;
}

function deepCloneEmployee(
  employee: ForemanEmployeeDispatch
): ForemanEmployeeDispatch {
  if (typeof structuredClone === 'function') {
    return structuredClone(employee) as ForemanEmployeeDispatch;
  }

  return JSON.parse(JSON.stringify(employee)) as ForemanEmployeeDispatch;
}

function deepCloneEquipment(
  equipment: ForemanEquipmentDispatch
): ForemanEquipmentDispatch {
  if (typeof structuredClone === 'function') {
    return structuredClone(equipment) as ForemanEquipmentDispatch;
  }

  return JSON.parse(JSON.stringify(equipment)) as ForemanEquipmentDispatch;
}

function isSelfEntryDispatch(
  employee: Partial<ForemanEmployeeDispatch> | null | undefined
): boolean {
  if (!employee || employee.bActive === false) {
    return false;
  }

  const anyEmployee = employee as unknown as {
    employeeJobID?: string | null;
    employeeID?: string | null;
    prospectID?: string | null;
  };

  const isStructuralSelfEntry =
    anyEmployee.employeeJobID == null &&
    anyEmployee.employeeID == null &&
    anyEmployee.prospectID == null;

  return (
    SELF_ENTRY_STATUSES.has(String(employee.dispatchStatus || '')) ||
    isStructuralSelfEntry
  );
}

function findSelfEntryDispatch(
  job: ForemanJobDetail | null | undefined
): ForemanEmployeeDispatch | undefined {
  return (job?.employees || []).find((employee) =>
    isSelfEntryDispatch(employee)
  );
}

function employeeLaborsHaveIsChanged(
  employee: ForemanEmployeeDispatch | undefined
): boolean {
  return (employee?.labors || []).some((labor) => labor.isChanged === true);
}

/**
 * Merges employee lists when persisting `selectedJob` into offline storage.
 * Self-entry always follows `selected`. For crew, if the cached job has
 * unsaved labors (`isChanged`) and the selected snapshot does not (typical
 * `getForemanJobDetail` payload), keep the cached row so Job Summary drafts
 * survive Individual flows that briefly swap `selectedJob` to an API detail.
 */
export function mergeEmployeeDispatchesForJobSnapshot(
  persistedEmployees: ForemanEmployeeDispatch[] | undefined,
  selectedEmployees: ForemanEmployeeDispatch[] | undefined
): ForemanEmployeeDispatch[] {
  const persisted = persistedEmployees || [];
  const selected = selectedEmployees || [];
  const pairs = new Map<
    string,
    {
      persisted?: ForemanEmployeeDispatch;
      selected?: ForemanEmployeeDispatch;
    }
  >();

  for (const e of persisted) {
    const id = String(e.foremanEmployeeJobID || '');
    if (!id) {
      continue;
    }
    pairs.set(id, { persisted: e, ...pairs.get(id) });
  }
  for (const e of selected) {
    const id = String(e.foremanEmployeeJobID || '');
    if (!id) {
      continue;
    }
    const cur = pairs.get(id) || {};
    pairs.set(id, { ...cur, selected: e });
  }

  const order = [
    ...new Set([
      ...selected.map((e) => String(e.foremanEmployeeJobID || '')),
      ...persisted.map((e) => String(e.foremanEmployeeJobID || '')),
    ]),
  ].filter(Boolean);

  const result: ForemanEmployeeDispatch[] = [];

  for (const id of order) {
    const pair = pairs.get(id);
    if (!pair) {
      continue;
    }
    const pe = pair.persisted;
    const se = pair.selected;
    if (!se && pe) {
      result.push(deepCloneEmployee(pe));
      continue;
    }
    if (!pe && se) {
      result.push(deepCloneEmployee(se));
      continue;
    }
    if (!pe || !se) {
      continue;
    }

    if (isSelfEntryDispatch(pe) || isSelfEntryDispatch(se)) {
      result.push(deepCloneEmployee(se));
      continue;
    }

    const pChanged = employeeLaborsHaveIsChanged(pe);
    const sChanged = employeeLaborsHaveIsChanged(se);
    if (pChanged && !sChanged) {
      result.push(deepCloneEmployee(pe));
    } else {
      result.push(deepCloneEmployee(se));
    }
  }

  return result;
}

function equipmentDispatchKey(eq: ForemanEquipmentDispatch): string {
  return String(eq.foremanEquipmentDispatchID ?? eq.equipmentDispatchID ?? '');
}

function equipmentUsagesHaveIsChanged(
  equipment: ForemanEquipmentDispatch | undefined
): boolean {
  return (equipment?.usages || []).some((u) => u.isChanged === true);
}

/**
 * The expensive per-employee clone merge is only needed when the cached job has
 * unsaved labors/usages but `selectedJob` looks like a thin API snapshot without
 * those flags — otherwise `deepCloneJob(selectedJob)` already matches intent.
 */
function needsCrewOrEquipmentThinSnapshotMerge(
  persistedJob: ForemanJobDetail,
  selectedJob: ForemanJobDetail
): boolean {
  const selectedByEmpId = new Map(
    (selectedJob.employees || []).map(
      (e) => [String(e.foremanEmployeeJobID || ''), e] as const
    )
  );

  for (const pe of persistedJob.employees || []) {
    if (isSelfEntryDispatch(pe)) {
      continue;
    }
    if (!employeeLaborsHaveIsChanged(pe)) {
      continue;
    }
    const id = String(pe.foremanEmployeeJobID || '');
    const se = selectedByEmpId.get(id);
    if (se && !employeeLaborsHaveIsChanged(se)) {
      return true;
    }
  }

  const selectedByEqId = new Map(
    (selectedJob.equipment || []).map(
      (eq) => [equipmentDispatchKey(eq), eq] as const
    )
  );

  for (const pe of persistedJob.equipment || []) {
    if (!equipmentUsagesHaveIsChanged(pe)) {
      continue;
    }
    const k = equipmentDispatchKey(pe);
    const se = selectedByEqId.get(k);
    if (se && !equipmentUsagesHaveIsChanged(se)) {
      return true;
    }
  }

  return false;
}

export function mergeEquipmentDispatchesForJobSnapshot(
  persistedEquipment: ForemanEquipmentDispatch[] | undefined,
  selectedEquipment: ForemanEquipmentDispatch[] | undefined
): ForemanEquipmentDispatch[] {
  const persisted = persistedEquipment || [];
  const selected = selectedEquipment || [];
  const pairs = new Map<
    string,
    {
      persisted?: ForemanEquipmentDispatch;
      selected?: ForemanEquipmentDispatch;
    }
  >();

  for (const eq of persisted) {
    const id = equipmentDispatchKey(eq);
    if (!id) {
      continue;
    }
    pairs.set(id, { persisted: eq, ...pairs.get(id) });
  }
  for (const eq of selected) {
    const id = equipmentDispatchKey(eq);
    if (!id) {
      continue;
    }
    const cur = pairs.get(id) || {};
    pairs.set(id, { ...cur, selected: eq });
  }

  const order = [
    ...new Set([
      ...selected.map((e) => equipmentDispatchKey(e)),
      ...persisted.map((e) => equipmentDispatchKey(e)),
    ]),
  ].filter(Boolean);

  const result: ForemanEquipmentDispatch[] = [];

  for (const id of order) {
    const pair = pairs.get(id);
    if (!pair) {
      continue;
    }
    const pe = pair.persisted;
    const se = pair.selected;
    if (!se && pe) {
      result.push(deepCloneEquipment(pe));
      continue;
    }
    if (!pe && se) {
      result.push(deepCloneEquipment(se));
      continue;
    }
    if (!pe || !se) {
      continue;
    }

    const pChanged = equipmentUsagesHaveIsChanged(pe);
    const sChanged = equipmentUsagesHaveIsChanged(se);
    if (pChanged && !sChanged) {
      result.push(deepCloneEquipment(pe));
    } else {
      result.push(deepCloneEquipment(se));
    }
  }

  return result;
}

/**
 * When an API-shaped snapshot clears self-entry default WO fields, we restore
 * from the prior persisted job. If the merged row still has active labors,
 * prefer phases that actually appear on those labors (ordered by the old
 * persisted default list) so a reduced Individual selection is not replaced by
 * a stale multi-default list from offline storage.
 */
function resolveSelfEntryDefaultWorkOrdersForEmptySnapshot(
  persistedSelf: ForemanEmployeeDispatch,
  mergedSelf: ForemanEmployeeDispatch,
  workOrders: ForemanPhase[] | undefined
): {
  foremanPhaseIDs: string[];
  foremanPhaseNames: string[];
  foremanPhaseID: string | null;
  foremanPhaseName: string | null;
} {
  const workOrderList = workOrders || [];
  const persistedNames = persistedSelf.foremanPhaseNames || [];

  const persistedIds = (persistedSelf.foremanPhaseIDs || [])
    .map((x) => String(x ?? '').trim())
    .filter(Boolean);
  const persistedRoot = String(persistedSelf.foremanPhaseID ?? '').trim();

  const nameForPhase = (id: string): string => {
    const wo = workOrderList.find(
      (w) => String(w.foremanPhaseID ?? '').trim() === id
    );
    if (wo?.phaseName) {
      return wo.phaseName as string;
    }
    const idx = persistedIds.indexOf(id);
    if (idx >= 0 && persistedNames[idx]) {
      return persistedNames[idx] as string;
    }
    return id;
  };

  const activePhaseIds = new Set<string>();
  for (const labor of mergedSelf.labors || []) {
    if (labor.bActive === false) {
      continue;
    }
    const id = String(labor.foremanPhaseID ?? '').trim();
    if (id) {
      activePhaseIds.add(id);
    }
  }

  let orderedIds: string[] = [];

  if (activePhaseIds.size > 0) {
    const seen = new Set<string>();
    for (const id of [...persistedIds, persistedRoot]) {
      if (id && activePhaseIds.has(id) && !seen.has(id)) {
        orderedIds.push(id);
        seen.add(id);
      }
    }
    for (const id of activePhaseIds) {
      if (!seen.has(id)) {
        orderedIds.push(id);
        seen.add(id);
      }
    }
  } else if (persistedIds.length > 0) {
    orderedIds = [...persistedIds];
  } else if (persistedRoot) {
    orderedIds = [persistedRoot];
  }

  const foremanPhaseNames = orderedIds.map(nameForPhase);

  return {
    foremanPhaseIDs: orderedIds,
    foremanPhaseNames,
    foremanPhaseID: orderedIds[0] ?? null,
    foremanPhaseName: foremanPhaseNames[0] ?? null,
  };
}

/**
 * Job Summary (and other flows) persist `selectedJob` via an effect. The API
 * snapshot often omits or clears Individual self-entry default work orders.
 * Merge selected onto a clone while restoring those defaults from the prior
 * persisted job when the snapshot dropped them.
 */
function mergeJobSnapshotPreservingSelfEntryDefaults(
  persistedJob: ForemanJobDetail,
  selectedJob: ForemanJobDetail
): ForemanJobDetail {
  const merged = deepCloneJob(selectedJob);

  if (needsCrewOrEquipmentThinSnapshotMerge(persistedJob, selectedJob)) {
    merged.employees = mergeEmployeeDispatchesForJobSnapshot(
      persistedJob.employees,
      selectedJob.employees
    );

    merged.equipment = mergeEquipmentDispatchesForJobSnapshot(
      persistedJob.equipment,
      selectedJob.equipment
    );
  }

  const persistedSelf = findSelfEntryDispatch(persistedJob);
  if (persistedSelf) {
    const mergedSelf = findSelfEntryDispatch(merged);

    if (mergedSelf) {
      const hasPersistedPhases =
        (persistedSelf.foremanPhaseIDs?.length ?? 0) > 0 ||
        !!persistedSelf.foremanPhaseID;
      const incomingPhasesEmpty =
        (mergedSelf.foremanPhaseIDs?.length ?? 0) === 0 &&
        !mergedSelf.foremanPhaseID;

      if (hasPersistedPhases && incomingPhasesEmpty) {
        const resolved = resolveSelfEntryDefaultWorkOrdersForEmptySnapshot(
          persistedSelf,
          mergedSelf,
          merged.workOrders
        );
        mergedSelf.foremanPhaseIDs = resolved.foremanPhaseIDs;
        mergedSelf.foremanPhaseNames = resolved.foremanPhaseNames;
        mergedSelf.foremanPhaseID = resolved.foremanPhaseID;
        mergedSelf.foremanPhaseName = resolved.foremanPhaseName;
      }
    } else {
      merged.employees = [...(merged.employees || [])];
      merged.employees.push(deepCloneEmployee(persistedSelf));
    }
  }

  return merged;
}

export function mergeSelectedJobIntoPersistedJobs(
  persistedJobs: ForemanJobDetail[] | null | undefined,
  selectedJob: ForemanJobDetail | null | undefined
): ForemanJobDetail[] {
  // Collapse duplicate self-entry rows per job on the way out (#30177). This is
  // the SECOND writer to `jobsCacheKey` (fired on every `selectedJob.set`), so it
  // must run the row-dedupe too — `cacheApiData` alone would only clean up on the
  // next online list refresh, leaving the persisted duplicate live offline. Mirrors
  // how #30176's job-dedupe is applied on both write paths.
  const collapseSelfEntry = (jobs: ForemanJobDetail[]): ForemanJobDetail[] =>
    jobs.map(dedupeSelfEntryEmployeeDispatches);

  // Collapse any dual-key residue before matching (#30176).
  const currentJobs = dedupeForemanJobsByIdentity(persistedJobs);

  if (!selectedJob) {
    return collapseSelfEntry(currentJobs);
  }

  if (!getForemanJobDetailKey(selectedJob)) {
    return collapseSelfEntry(currentJobs);
  }

  const matchingIndex = currentJobs.findIndex((job) =>
    jobsShareForemanIdentity(job, selectedJob)
  );

  if (matchingIndex === -1) {
    currentJobs.push(selectedJob);
    return collapseSelfEntry(dedupeForemanJobsByIdentity(currentJobs));
  }

  const existing = currentJobs[matchingIndex];
  currentJobs[matchingIndex] = mergeJobSnapshotPreservingSelfEntryDefaults(
    existing,
    selectedJob
  );
  return collapseSelfEntry(dedupeForemanJobsByIdentity(currentJobs));
}
